import {
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
  type ReactNode,
} from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Checkbox,
  FieldSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@micronet/ui-kit';
import type { ChartConfig, ChartType, DataSeries, DataPoint } from '../types/dashboard';
import type { ChartOptions, FilterSpec } from '../charts/model';
import { listCharts, getChart } from '../charts';
import { uniqueValues } from '../charts/shared/uniqueValues';
import { useDashboardData } from '../hooks/useDashboardData';
import { FiltersEditor } from './FiltersEditor';
import { ChartCard } from './ChartCard';

interface WidgetEditorProps {
  chart?: ChartConfig | null;
  /** Required by the standalone Dialog, which owns Cancelar/Guardar. Both are
   *  unreachable under `embed` — the host owns the chrome — so they are optional
   *  there rather than props a host has to invent no-ops for. */
  onClose?: () => void;
  onSave?: (cfg: ChartConfig) => void;
  /**
   * 'full' (default) edits everything. 'presentation' hides the data wiring
   * (dataset, encoding, series structure, filters) and exposes only
   * presentation — title, axis labels, colors, limits and display options —
   * for consumers whose data shape is fixed (e.g. an algorithm output) and
   * must not be repointed.
   */
  editingScope?: 'full' | 'presentation';
  /** Restrict the type dropdown. Either a fixed list, or a function of the chart
   *  being edited (e.g. types compatible with that chart's data shape). */
  allowedChartTypes?: ChartType[] | ((chart: ChartConfig) => ChartType[]);
  /** Render the editor body + preview WITHOUT the Dialog chrome, so it can be
   *  embedded (e.g. a "Presentación" tab inside a host modal). The host provides
   *  the modal, the title, the scroll region and the save action — it reads the
   *  edited config off the ref (see WidgetEditorHandle) and applies it itself.
   *  The editor never calls `onSave` in this mode. */
  embed?: boolean;
}

/** What a host holding a ref to the editor can ask it for. Lets an embedded
 *  editor keep its own draft state while the save button lives outside it. */
export interface WidgetEditorHandle {
  /** The config as currently edited, including unsaved changes. */
  getConfig: () => ChartConfig;
}

/**
 * Density for the form column. From `lg` up it only gets 26rem of the modal
 * (below that it spans the width), so its controls
 * run a notch smaller than the kit's defaults (h-9 → h-8, sm → xs) and closer
 * together — more of the form lands above the fold before the scroll starts.
 * Applied by descendant selector rather than per-field so a field added later
 * inherits it. `input` and `[role=combobox]` (the Radix select trigger) are
 * named on purpose: a blanket `button` rule would resize the checkboxes too.
 */
const COMPACT_FORM = [
  'space-y-3 text-sm',
  '[&_input]:h-8 [&_input]:text-xs',
  '[&_label]:text-xs',
  '[&_[role=combobox]]:h-8 [&_[role=combobox]]:text-xs',
].join(' ');

/**
 * Accessible name for a control inside the series table.
 *
 * The column headings are plain text in a grid, not `<th>`/`<label>`, so nothing
 * associates them with the cells below. Without this, a screen reader announces
 * every row identically ("combobox, Izquierdo") and the rows are impossible to
 * tell apart. Falls back to the position when a series has no name yet.
 */
function seriesControlLabel(column: string, series: DataSeries, index: number): string {
  return `${column} — ${series.name?.trim() || `serie ${index + 1}`}`;
}

/** One column of the series table: its heading, its track width, and the control
 *  it renders for a given series. */
interface SeriesColumn {
  key: string;
  label: string;
  /** A CSS grid track, shared by the heading row and every series row. */
  width: string;
  cell: (series: DataSeries, index: number) => ReactNode;
}

// Fixed enum choices for the per-series presentation controls. `'left'` / `'solid'`
// are the implicit defaults, so the editor stores `undefined` for them (keeps saved
// configs minimal and preserves the spec's backward-compat invariant).
const Y_AXIS_OPTIONS = [
  { value: 'left' as const, label: 'Izquierdo' },
  { value: 'right' as const, label: 'Derecho' },
];
const DASH_OPTIONS = [
  { value: 'solid' as const, label: 'Sólido' },
  { value: 'dash' as const, label: 'Guion' },
  { value: 'dot' as const, label: 'Punto' },
  { value: 'dashdot' as const, label: 'Guion-punto' },
];

/**
 * The stats box's four reachable placements, each mapped to the option triple the
 * layout reads. Kept as data (not three checkboxes) because the booleans are not
 * independent: `statsBoxColumns` only means anything together with
 * `statsBoxRight`, and neither means anything without `showStatsBox`.
 */
const STATS_BOX_PLACEMENTS = {
  // NOT "hidden": `showStatsBox: false` is exactly the flag ChartCard uses to
  // decide to render the statistics as a footer strip under the plot, so this
  // moves them out of the figure rather than removing them — and the footer does
  // not travel into the PNG export. Every placement writes all THREE booleans:
  // leaving a stale `statsBoxRight` behind still shrank the plot to the gutter
  // domain, blanking 40% of it.
  pie: {
    label: 'En el pie de la tarjeta',
    options: { showStatsBox: false, statsBoxRight: false, statsBoxColumns: false },
  },
  dentro: {
    label: 'Dentro del gráfico',
    options: { showStatsBox: true, statsBoxRight: false, statsBoxColumns: false },
  },
  derecha: {
    label: 'A la derecha (apilada)',
    options: { showStatsBox: true, statsBoxRight: true, statsBoxColumns: false },
  },
  columnas: {
    label: 'A la derecha (en columnas)',
    options: { showStatsBox: true, statsBoxRight: true, statsBoxColumns: true },
  },
} as const satisfies Record<
  string,
  // The full triple, not Partial: a placement that omitted one of them left the
  // previous value merged into the config, which is how "hidden" kept the gutter.
  {
    label: string;
    options: Required<Pick<ChartOptions, 'showStatsBox' | 'statsBoxRight' | 'statsBoxColumns'>>;
  }
>;

type StatsBoxPlacement = keyof typeof STATS_BOX_PLACEMENTS;

const STATS_BOX_PLACEMENT_CHOICES = (Object.keys(STATS_BOX_PLACEMENTS) as StatsBoxPlacement[]).map(
  (value) => ({ value, label: STATS_BOX_PLACEMENTS[value].label })
);

/** Which placement the current options represent (the inverse of the map above). */
function statsBoxPlacement(options: ChartOptions | undefined): StatsBoxPlacement {
  if (!options?.showStatsBox) return 'pie';
  if (!options.statsBoxRight) return 'dentro';
  return options.statsBoxColumns ? 'columnas' : 'derecha';
}

/**
 * A `Select` over a fixed enum whose visible label differs from its stored value
 * (e.g. "Derecho" → `'right'`). `FieldSelect` can't be reused here because it maps
 * each option's label to its own value; this composes the lower-level parts.
 */
function EnumSelect<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  /** Needed inside the series table, where the column heading is a plain div and
   *  the trigger would otherwise be announced as just its current value. */
  ariaLabel?: string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        // Narrow the string back to T instead of casting: the rendered SelectItem
        // values all come from `options`, so a match always exists on the happy
        // path; anything unexpected is ignored rather than propagated as a lie.
        const hit = options.find((o) => o.value === v);
        if (hit) onChange(hit.value);
      }}
    >
      <SelectTrigger aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Per-series / per-category color control: a native color picker plus an "Auto"
 * reset. `undefined` means "no explicit color" → the chart falls back to the
 * default palette. Used for both `series[].color` and `groupColors[cat]`.
 */
function ColorPicker({
  value,
  onChange,
  ariaLabel = 'Color',
}: {
  value?: string;
  onChange: (color: string | undefined) => void;
  /** Overridden inside the series table so each row names its own series. */
  ariaLabel?: string;
}) {
  const isAuto = !value;
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={ariaLabel}
        value={value ?? '#3B82F6'}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white p-0.5"
      />
      <button
        type="button"
        onClick={() => onChange(undefined)}
        className={
          isAuto ? 'text-xs font-medium text-gray-900' : 'text-xs text-gray-500 hover:text-gray-800'
        }
      >
        Auto
      </button>
    </div>
  );
}

export const WidgetEditor = forwardRef<WidgetEditorHandle, WidgetEditorProps>(function WidgetEditor(
  { chart, onClose, onSave, editingScope = 'full', allowedChartTypes, embed = false },
  ref
) {
  const { datasets, getDataset, getDatasetFields, datasetIds } = useDashboardData();
  const isPresentation = editingScope === 'presentation';

  const [local, setLocal] = useState<ChartConfig>(
    () =>
      chart ?? {
        id: `widget-${Date.now()}`,
        type: 'line',
        title: 'Nuevo Widget',
        encoding: { xKey: 'x' },
        datasetId: datasetIds[0] ?? undefined,
      }
  );

  // The selected type's capabilities drive which encoding fields are shown. No
  // per-type knowledge is hardcoded here — adding a chart type surfaces its
  // fields automatically and the type dropdown is sourced from the registry.
  const chartTypes = useMemo(() => listCharts(), []);
  const visibleTypes = useMemo(() => {
    const allow =
      typeof allowedChartTypes === 'function' ? allowedChartTypes(local) : allowedChartTypes;
    return allow ? chartTypes.filter((d) => allow.includes(d.id)) : chartTypes;
  }, [chartTypes, allowedChartTypes, local]);
  const capabilities = getChart(local.type)?.capabilities;

  const selectedDatasetId = local.datasetId;
  const availableFields = useMemo(() => {
    const s = new Set<string>();
    if (local.data && local.data.length > 0) {
      local.data.forEach((row) => Object.keys(row).forEach((k) => s.add(k)));
    } else if (selectedDatasetId) {
      getDatasetFields(selectedDatasetId).forEach((k) => s.add(k));
    } else {
      Object.values(datasets).forEach((rows) =>
        rows.forEach((row) => Object.keys(row).forEach((k) => s.add(k)))
      );
    }
    return Array.from(s);
  }, [selectedDatasetId, local.data, datasets, getDatasetFields]);

  // The host's save button lives outside this component (embedded mode), so it
  // needs a way to read the draft at the moment it's clicked. A ref keeps that
  // read pull-based: lifting `local` up would re-render the host — and with it
  // the Plotly preview — on every keystroke.
  useImperativeHandle(ref, () => ({ getConfig: () => local }), [local]);

  const setField = <K extends keyof ChartConfig>(k: K, v: ChartConfig[K]) =>
    setLocal((prev) => ({ ...prev, [k]: v }));

  const setEncoding = (k: 'xKey' | 'yKey' | 'groupKey', v: string | undefined) =>
    setLocal((prev) => ({ ...prev, encoding: { ...(prev.encoding ?? {}), [k]: v } }));

  const updateSeries = (i: number, patch: Partial<DataSeries>) => {
    const next = (local.series ?? []).slice();
    next[i] = { ...next[i], ...patch } as DataSeries;
    setLocal({ ...local, series: next });
  };

  const addSeries = () => {
    const defaultKey = availableFields[0] ?? 'value';
    const next = [...(local.series ?? []), { dataKey: defaultKey, name: defaultKey }];
    setLocal({ ...local, series: next });
  };

  const removeSeries = (i: number) => {
    const next = (local.series ?? []).filter((_, idx) => idx !== i);
    setLocal({ ...local, series: next });
  };

  const toggleLimits = (enable: boolean) => {
    if (!enable) {
      const copy = { ...local } as Partial<ChartConfig>;
      delete copy.limits;
      setLocal(copy as ChartConfig);
      return;
    }
    if (!local.limits) setLocal({ ...local, limits: { upper: undefined, lower: undefined } });
  };

  const setLimit = (k: 'upper' | 'lower', raw: string) =>
    setLocal((prev) => {
      // Only keep finite numbers; a blank or non-numeric entry clears the bound.
      // Mirrors FiltersEditor.parseBound so a NaN never reaches resolveLimitsShapes.
      const n = Number(raw);
      const value = raw.trim() === '' || !Number.isFinite(n) ? undefined : n;
      return { ...prev, limits: { ...(prev.limits ?? {}), [k]: value } };
    });

  const setOption = <K extends keyof ChartOptions>(k: K, v: ChartOptions[K]) =>
    setLocal((prev) => ({ ...prev, options: { ...(prev.options ?? {}), [k]: v } }));

  const setOptions = (patch: Partial<ChartOptions>) =>
    setLocal((prev) => ({ ...prev, options: { ...(prev.options ?? {}), ...patch } }));

  const setFilters = (filters: FilterSpec[]) => setLocal((prev) => ({ ...prev, filters }));

  const setGroupColor = (category: string, color: string | undefined) =>
    setLocal((prev) => {
      const next = { ...(prev.groupColors ?? {}) };
      if (color) next[category] = color;
      else delete next[category];
      return { ...prev, groupColors: next };
    });

  // The rows backing the current source (inline data wins over a datasetId),
  // shared by the per-category color editor and the filter value pickers.
  const currentRows = useMemo<DataPoint[]>(
    () =>
      local.data && local.data.length > 0
        ? local.data
        : local.datasetId
          ? getDataset(local.datasetId)
          : [],
    [local.data, local.datasetId, getDataset]
  );

  // Distinct values of the selected groupKey, for the per-category color editor.
  const groupKey = local.encoding?.groupKey;
  const groupValues = useMemo(
    () => (groupKey ? uniqueValues(currentRows, groupKey) : []),
    [groupKey, currentRows]
  );

  // Distinct values of any column, for the categorical filter value picker.
  const getFilterValues = useCallback(
    (field: string) => uniqueValues(currentRows, field),
    [currentRows]
  );

  // The right Y-axis is emitted by shared/layout only when a series is actually
  // assigned to it (`yAxis: 'right'`). Gate its title input on the same
  // condition so a user can't label an axis that will never render.
  const hasRightAxisSeries = useMemo(
    () => (local.series ?? []).some((s) => s.yAxis === 'right'),
    [local.series]
  );

  // The series table's columns, derived from scope + capabilities. The header and
  // every row render from THIS list, sharing one grid template, so the two can't
  // drift apart and a hidden control takes its column heading with it.
  //
  // Track widths are scope-dependent because the table sits in a different place
  // per scope: presentation shows up to four columns inside the 26rem form
  // column (Eje Y and Trazo are capability-gated, so a bar chart shows two), cut
  // to what the compact controls need; full scope adds the data-wiring columns,
  // which only fit across the whole body.
  const seriesColumnSlots: (SeriesColumn | false | undefined)[] = [
    !isPresentation && {
      key: 'dataKey',
      label: 'Columna',
      width: 'minmax(9rem,1fr)',
      cell: (s: DataSeries, i: number) => (
        <FieldSelect
          value={s.dataKey}
          onChange={(v) => updateSeries(i, { dataKey: v ?? '' })}
          options={availableFields}
          aria-label={seriesControlLabel('Columna', s, i)}
        />
      ),
    },
    {
      key: 'name',
      label: 'Nombre',
      // A floor, not `minmax(0,...)`: the flexible track collapsed to ~19px on a
      // narrow viewport, and the horizontal escape hatch never triggered because
      // the row shrank instead of overflowing.
      width: isPresentation ? 'minmax(8rem,1fr)' : 'minmax(12rem,24rem)',
      cell: (s: DataSeries, i: number) => (
        <Input
          value={s.name}
          placeholder="Nombre"
          aria-label={seriesControlLabel('Nombre', s, i)}
          onChange={(e) => updateSeries(i, { name: e.target.value })}
        />
      ),
    },
    capabilities?.supportsSecondaryYAxis && {
      key: 'yAxis',
      label: 'Eje Y',
      width: isPresentation ? '6.25rem' : '8rem',
      cell: (s: DataSeries, i: number) => (
        <EnumSelect
          value={s.yAxis ?? 'left'}
          options={Y_AXIS_OPTIONS}
          ariaLabel={seriesControlLabel('Eje Y', s, i)}
          onChange={(v) => updateSeries(i, { yAxis: v === 'left' ? undefined : v })}
        />
      ),
    },
    capabilities?.supportsLineStyle && {
      key: 'dash',
      label: 'Trazo',
      width: isPresentation ? '6.5rem' : '8rem',
      cell: (s: DataSeries, i: number) => (
        <EnumSelect
          value={s.dash ?? 'solid'}
          options={DASH_OPTIONS}
          ariaLabel={seriesControlLabel('Trazo', s, i)}
          onChange={(v) => updateSeries(i, { dash: v === 'solid' ? undefined : v })}
        />
      ),
    },
    {
      key: 'color',
      label: 'Color',
      width: isPresentation ? '5rem' : '11rem',
      cell: (s: DataSeries, i: number) => (
        <ColorPicker
          value={s.color}
          ariaLabel={seriesControlLabel('Color', s, i)}
          onChange={(c) => updateSeries(i, { color: c })}
        />
      ),
    },
    !isPresentation && {
      key: 'errorKey',
      label: 'Error ±',
      width: 'minmax(9rem,1fr)',
      cell: (s: DataSeries, i: number) => (
        <FieldSelect
          value={s.errorKey}
          onChange={(v) => updateSeries(i, { errorKey: v })}
          options={availableFields}
          noneLabel="(sin error)"
          placeholder="Error ±"
          aria-label={seriesControlLabel('Error ±', s, i)}
        />
      ),
    },
    !isPresentation && {
      key: 'remove',
      label: '',
      width: '2.5rem',
      cell: (s: DataSeries, i: number) => (
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive"
          aria-label={seriesControlLabel('Eliminar', s, i)}
          onClick={() => removeSeries(i)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];
  const seriesColumns = seriesColumnSlots.filter((c): c is SeriesColumn => Boolean(c));
  // Full scope spans the whole body, where a trailing empty track parks the
  // leftover width — without it the `1fr` columns (Columna, Error ±) stretch
  // across the body. Presentation fills the form column exactly, so there is no
  // slack to park and the name track is the one that takes up the remainder.
  const seriesTemplate = [
    ...seriesColumns.map((c) => c.width),
    ...(isPresentation ? [] : ['minmax(0,1fr)']),
  ].join(' ');

  // Series — only for types that accept multiple series. In presentation scope
  // up to four columns are editable (name, axis, dash, color — the middle two
  // are capability-gated) and they fit the form column, so the block sits there
  // with the rest of the fields. Full scope adds the data-wiring columns, which
  // only fit across the whole body, below the grid.
  //
  // Hidden entirely when there is nothing to list AND nothing to add: a lone
  // "Series" heading over empty space reads as a section that failed to load.
  const hasSeries = (local.series ?? []).length > 0;
  const seriesBlock = capabilities?.multiSeries && (hasSeries || !isPresentation) ? (
    <div>
      <div className="flex items-center justify-between">
        <Label>Series</Label>
        {!isPresentation && (
          <Button variant="link" size="sm" onClick={addSeries}>
            <Plus className="h-4 w-4" /> Agregar serie
          </Button>
        )}
      </div>

      {!hasSeries && !isPresentation && (
        <p className="mt-1 text-xs text-muted-foreground">
          Sin series. Agrega una para trazar una columna de datos.
        </p>
      )}

      {hasSeries && (
        // Horizontal-only escape hatch for the wide (full-scope) table.
        // `overflow-y-hidden` is load-bearing — CSS promotes a `visible`
        // cross-axis to `auto`, which would make this a second vertical
        // scroller inside the host's (US 2074).
        <div className="mt-1 overflow-x-auto overflow-y-hidden">
          <div className={isPresentation ? 'space-y-1' : 'min-w-[42rem] space-y-2'}>
            <div
              className="grid items-center gap-2 px-1 text-xs text-muted-foreground"
              style={{ gridTemplateColumns: seriesTemplate }}
            >
              {seriesColumns.map((c) => (
                <div key={c.key}>{c.label}</div>
              ))}
            </div>
            {(local.series ?? []).map((s, i) => (
              <div
                key={i}
                className="grid items-center gap-2"
                style={{ gridTemplateColumns: seriesTemplate }}
              >
                {seriesColumns.map((c) => (
                  <div key={c.key}>{c.cell(s, i)}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : null;

  // Form (left) + live preview (right); in full scope the series table follows
  // below at full width, in presentation it sits inside the form column. The
  // preview takes every pixel the fixed-width form leaves, so widening the host
  // modal widens the chart, not the input column. Shared by the Dialog and
  // embedded renders. Nothing here scrolls VERTICALLY on its own: whoever mounts
  // the body owns the single scroll region (the Dialog below, or the host) —
  // US 2074. The series table keeps a horizontal-only escape hatch.
  const bodyGrid = (
    <div className="grid gap-6 lg:grid-cols-[26rem_minmax(0,1fr)]">
      <div className={COMPACT_FORM}>
        <div className="space-y-1">
          <Label htmlFor="we-title">Título</Label>
          <Input
            id="we-title"
            value={local.title}
            onChange={(e) => setField('title', e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="we-subtitle">Subtítulo</Label>
          <Input
            id="we-subtitle"
            value={local.subtitle ?? ''}
            onChange={(e) => setField('subtitle', e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label>Tipo de gráfico</Label>
          <Select
            value={local.type}
            onValueChange={(v) => setField('type', v as ChartConfig['type'])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleTypes.map((def) => (
                <SelectItem key={def.id} value={def.id}>
                  {def.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Source — hidden in presentation scope (the data shape is fixed). */}
        {!isPresentation && (
          <div className="space-y-1">
            <Label>Fuente de datos</Label>
            <FieldSelect
              value={local.datasetId}
              onChange={(v) =>
                // Selecting a source switches it: drop any inline data, which
                // normalizeConfig would otherwise keep prioritizing.
                setLocal((prev) => ({ ...prev, datasetId: v, data: undefined }))
              }
              options={datasetIds}
              noneLabel="(ninguno)"
            />
            {local.data && local.data.length > 0 && !local.datasetId && (
              <p className="text-sm text-muted-foreground">
                Nota: este widget contiene datos embebidos (inline). El selector muestra
                &quot;(ninguno)&quot; porque no está referenciando una fuente de datos externa.
              </p>
            )}
          </div>
        )}

        {/* X axis: its data column (full scope only) paired with its display name.
              Both gated by the type's capability, so types without an X axis (e.g.
              pie) show neither. */}
        {capabilities?.supportsXKey && (
          <div className={!isPresentation ? 'grid grid-cols-2 gap-3' : 'space-y-1'}>
            {!isPresentation && (
              <div className="space-y-1">
                <Label>Columna del eje X</Label>
                <FieldSelect
                  value={local.encoding?.xKey}
                  onChange={(v) => setEncoding('xKey', v)}
                  options={availableFields}
                  noneLabel="(ninguno)"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="we-xlabel">Nombre del eje X</Label>
              <Input
                id="we-xlabel"
                value={local.xAxisLabel ?? ''}
                onChange={(e) => setField('xAxisLabel', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Y axis: data column (full scope only) + display name, same pattern
              as X — except the two answer DIFFERENT questions here. `supportsYKey`
              is "is there a single Y column to bind?", which a multi-series type
              says no to because its Y comes from the series; having a Y axis worth
              naming is the wider condition, and gating the name on the binding left
              line+bar with an unnameable left axis beside an editable right one.
              Charts drawn as stacked panels are the exception: each panel titles
              its own axis from `options.panels[].label`, so a figure-wide Y name
              would be an input that changes nothing. Keyed off the panels in the
              config rather than the type, so it stays capability/data-driven. */}
        {(capabilities?.supportsYKey || capabilities?.multiSeries) &&
          !local.options?.panels?.length && (
          <div
            className={
              !isPresentation && capabilities?.supportsYKey ? 'grid grid-cols-2 gap-3' : 'space-y-1'
            }
          >
            {!isPresentation && capabilities?.supportsYKey && (
              <div className="space-y-1">
                <Label>Columna del eje Y</Label>
                <FieldSelect
                  value={local.encoding?.yKey}
                  onChange={(v) => setEncoding('yKey', v)}
                  options={availableFields}
                  noneLabel={capabilities.multiSeries ? '(usar series)' : '(ninguno)'}
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="we-ylabel">Nombre del eje Y</Label>
              <Input
                id="we-ylabel"
                value={local.yAxisLabel ?? ''}
                onChange={(e) => setField('yAxisLabel', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Category column — no axis-name counterpart; pure data wiring. */}
        {!isPresentation && capabilities?.supportsGroupBy && (
          <div className="space-y-1">
            <Label>Columna de categoría</Label>
            <FieldSelect
              value={local.encoding?.groupKey}
              onChange={(v) => setEncoding('groupKey', v)}
              options={availableFields}
              noneLabel="(ninguno)"
            />
          </div>
        )}

        {/* Secondary (right) Y-axis name — shown only once a series is assigned
              to the right axis, since that's when the axis actually renders. */}
        {capabilities?.supportsSecondaryYAxis && hasRightAxisSeries && (
          <div className="space-y-1">
            <Label htmlFor="we-y2label">Nombre del eje Y derecho</Label>
            <Input
              id="we-y2label"
              value={local.y2AxisLabel ?? ''}
              onChange={(e) => setField('y2AxisLabel', e.target.value)}
            />
          </div>
        )}

        {/* Series, in presentation scope only — its four columns fit here and
              read as one more field of this column. Full scope renders it below
              the grid instead (see body), where its seven columns fit. */}
        {isPresentation && seriesBlock}

        {/* Limits (upper/lower) — only for types that honor them, and only in
              full scope: reference bands are a chart-authoring decision, not one
              to re-open on a published figure. Limits already saved keep
              rendering; this hides the control, it doesn't clear them. */}
        {!isPresentation && capabilities?.supportsLimits && (
          <div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="we-limits"
                checked={!!local.limits}
                onCheckedChange={(v) => toggleLimits(v === true)}
              />
              <Label htmlFor="we-limits">Agregar límites (eje Y)</Label>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Dibuja líneas de referencia horizontales sobre el eje Y: un umbral superior y uno
              inferior, con una banda sombreada entre ambos.
            </p>
            {local.limits && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="we-upper">Límite superior</Label>
                  <Input
                    id="we-upper"
                    type="number"
                    value={local.limits?.upper ?? ''}
                    onChange={(e) => setLimit('upper', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="we-lower">Límite inferior</Label>
                  <Input
                    id="we-lower"
                    type="number"
                    value={local.limits?.lower ?? ''}
                    onChange={(e) => setLimit('lower', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-category color overrides — for grouped charts with a groupKey. */}
        {capabilities?.supportsGroupBy && groupKey && groupValues.length > 0 && (
          <div>
            <Label>Colores por categoría</Label>
            <div className="mt-2 space-y-2">
              {groupValues.map((cat) => (
                <div key={cat} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-sm">{cat}</span>
                  <div className="w-36">
                    <ColorPicker
                      value={local.groupColors?.[cat]}
                      onChange={(c) => setGroupColor(cat, c)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters — transversal row subsetting, for filterable types. Hidden
              in presentation scope (subsetting is data wiring, not presentation). */}
        {!isPresentation && capabilities?.filterable && (
          <FiltersEditor
            filters={local.filters ?? []}
            fields={availableFields}
            getValues={getFilterValues}
            onChange={setFilters}
          />
        )}

        {/* The display-option checkboxes are not here: each one changes how the
              figure reads, so they sit under the preview itself (the block below
              ChartCard). What stays is the stats-box placement, a select that
              belongs with the form's other fields — as do the limits and filter
              checkboxes above, which are data wiring, not display. */}
        <div className="space-y-2">
          {/* Stats box placement. Collapses the three booleans the layout
                actually reads into one choice, so "inside + columns" (which the
                layout ignores — columns only apply in the gutter) can't be
                expressed. Only offered when the chart carries statistics. */}
          {(local.statistics?.length ?? 0) > 0 && (
            <div className="space-y-1">
              {/* No htmlFor: EnumSelect renders a Radix trigger and forwards no id, so a
                      label pointing at "we-statsbox" referenced nothing. */}
              <Label>Caja de estadísticas</Label>
              <EnumSelect
                value={statsBoxPlacement(local.options)}
                onChange={(v) => setOptions(STATS_BOX_PLACEMENTS[v].options)}
                options={STATS_BOX_PLACEMENT_CHOICES}
              />
            </div>
          )}
        </div>

        {/* Validation: single-series x/y charts need their keys present in the
              data. Capability-driven — no specific chart type is named. Skipped in
              presentation scope (data wiring is fixed and hidden). */}
        {!isPresentation &&
          capabilities &&
          !capabilities.multiSeries &&
          capabilities.supportsXKey &&
          capabilities.supportsYKey &&
          (() => {
            const sampleRows =
              local.data && local.data.length > 0
                ? local.data
                : local.datasetId
                  ? getDataset(local.datasetId)
                  : [];
            const xk = local.encoding?.xKey;
            const yk = local.encoding?.yKey;
            if (!xk || !yk) return null;
            if (!sampleRows || sampleRows.length === 0) {
              return (
                <div className="rounded bg-warning/10 p-2 text-sm text-warning-foreground">
                  <strong>Atención:</strong> No hay datos disponibles para la fuente seleccionada.
                </div>
              );
            }
            const countHasX = sampleRows.filter((r) => r[xk] != null).length;
            const countHasY = sampleRows.filter((r) => r[yk] != null).length;
            if (countHasX === 0 || countHasY === 0) {
              return (
                <div className="rounded bg-warning/10 p-2 text-sm text-warning-foreground">
                  <strong>Atención:</strong> Las columnas seleccionadas no existen en los datos.
                </div>
              );
            }
            return null;
          })()}
      </div>

      {/* Live preview — the same ChartCard the dashboard (and conciliatron, via
              inline data in presentation scope) renders, so the editor is WYSIWYG.
              The card itself is hidden below lg to keep narrow screens form-only;
              the options under it stay reachable there. */}
      <div className="self-start lg:sticky lg:top-0">
        <p className="mb-2 hidden text-xs uppercase tracking-wide text-muted-foreground lg:block">
          Vista previa
        </p>
        <div className="hidden lg:block">
          <ChartCard config={local} draggable={false} />
        </div>
        {/* Every display toggle lives here, next to the figure it changes,
                instead of buried at the bottom of the form column. Which ones
                appear is capability-driven, so a type that can't stack bars
                doesn't offer the switch. */}
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Checkbox
              id="we-legend"
              checked={local.options?.showLegend ?? capabilities?.legendByDefault ?? false}
              onCheckedChange={(v) => setOption('showLegend', v === true)}
            />
            <Label htmlFor="we-legend">Mostrar leyenda</Label>
          </div>
          {capabilities?.supportsStacked && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="we-stacked"
                checked={local.options?.stacked ?? false}
                onCheckedChange={(v) => setOption('stacked', v === true)}
              />
              <Label htmlFor="we-stacked">Barras apiladas</Label>
            </div>
          )}
          {capabilities?.supportsDataLabels && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="we-datalabels"
                checked={local.options?.showDataLabels ?? false}
                onCheckedChange={(v) => setOption('showDataLabels', v === true)}
              />
              <Label htmlFor="we-datalabels">Etiquetas de datos</Label>
            </div>
          )}
          {/* Text fitting — on by default. The escape hatch for when the
                automatic line breaks land badly: off renders each label as one
                run (the old behaviour), and the fix is a shorter label. */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="we-autofit"
              checked={local.options?.autoFitText ?? true}
              onCheckedChange={(v) => setOption('autoFitText', v === true)}
            />
            <Label htmlFor="we-autofit">Ajustar textos al ancho del gráfico</Label>
          </div>
        </div>
      </div>
    </div>
  );

  const body = (
    <div className="space-y-6">
      {bodyGrid}
      {!isPresentation && seriesBlock}
    </div>
  );

  // Embedded (e.g. a "Presentación" tab inside a host modal): the body alone.
  // The host owns the modal, the title, the scroll region AND the actions — it
  // reads the edited config through the ref rather than hosting a save button
  // that would scroll away with the form.
  if (embed) {
    return body;
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      {/* Capped to the viewport with the body as the only scroller, so the title
          and the actions stay put while the form scrolls under them. */}
      <DialogContent className="flex max-h-[round(down,90vh,1px)] max-w-7xl flex-col">
        <DialogHeader>
          <DialogTitle>Configuración del gráfico</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">{body}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => onSave?.(local)}>
            <Save className="h-4 w-4" /> Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default WidgetEditor;
